<template>
    <div class="rental-form-video-uploader">
        <input
            ref="picker"
            type="file"
            class="rental-form-video-input"
            accept="video/mp4,video/quicktime,.mp4,.mov"
            style="display:none"
            @change="onFilePicked"
        />

        <div class="theme-gallery">
            <draggable
                tag="ul"
                class="theme-gallery__list"
                v-model="tiles"
                :draggable="'.theme-gallery__item--draggable'"
                v-bind="dragOpts"
                @end="onDragEnd"
            >
                <li
                    v-for="(tile, index) in tiles"
                    :key="tile.recordId"
                    class="theme-gallery__item theme-gallery__item--draggable"
                    :title="tile.originalFilename"
                >
                    <div class="theme-gallery__item-inner" style="position: relative;">
                        <img
                            v-if="tile.thumbnailUrl"
                            :src="tile.thumbnailUrl"
                            :alt="tile.originalFilename"
                            class="theme-gallery__thumbnail"
                            style="object-fit: cover;"
                        />
                        <div
                            v-else
                            class="theme-gallery__thumbnail"
                            style="display: flex; align-items: center; justify-content: center; text-align: center;"
                        >
                            <i class="icon-file" style="font-size: 28px; display:block;"></i>
                        </div>
                        <button
                            type="button"
                            class="theme-gallery__close"
                            :aria-label="i18n.removeLabel"
                            :title="i18n.removeLabel"
                            style="z-index: 2; font-weight: bold; line-height: 1;"
                            @click.prevent.stop="removeTile(tile)"
                        ><span aria-hidden="true">&times;</span></button>
                        <span
                            v-if="tile.status"
                            class="theme-gallery__status"
                            :class="'theme-gallery__status--' + statusCssState(tile.status)"
                            :title="statusLabel(tile)"
                            style="position: absolute; bottom: 4px; left: 4px; padding: 2px 6px; font-size: 11px; line-height: 1; border-radius: 3px; z-index: 2;"
                        >{{ statusLabel(tile) }}</span>
                        <div
                            v-if="tile.status === 'failed'"
                            class="theme-gallery__failed-overlay"
                            style="position: absolute; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; gap: 8px; z-index: 3;"
                        >
                            <button
                                type="button"
                                class="theme-btn theme-btn--small theme-btn--white"
                                :title="i18n.retryLabel"
                                @click.prevent.stop="retryTile(tile)"
                            >{{ i18n.retryLabel }}</button>
                        </div>
                    </div>
                    <div class="theme-gallery__item-index">{{ index + 1 }}</div>
                    <p
                        v-if="tile.status === 'failed' && tile.errorMessage"
                        class="text-xs text-red-700 mt-1"
                        style="max-width: 120px; word-break: break-word;"
                    >{{ tile.errorMessage }}</p>
                </li>
                <li
                    slot="footer"
                    class="theme-gallery__item-to-drop"
                    :title="i18n.addVideoLabel"
                    :aria-label="i18n.addVideoLabel"
                    @click="openPicker"
                >
                    <i class="icon-plus" style="font-size: 20px; margin-bottom: 6px;"></i>
                    <span>{{ i18n.addVideoLabel }}</span>
                </li>
            </draggable>
        </div>
    </div>
</template>

<script>
import $ from "jquery";
import draggable from "vuedraggable";

const MAX_POLL_ERRORS = 5;
const MAX_POLL_DURATION_MS = 8 * 60 * 1000; // 8 minutes from first processing observation

export default {
    props: {
        draftPropertyId: { default: null },
        uploadUrlBase: { type: String, default: '' },
        statusUrlTemplate: { type: String, default: '' },
        deleteUrlTemplate: { type: String, default: '' },
        deleteAllUrlTemplate: { type: String, default: '' },
        draftVideos: { type: Array, default: function () { return []; } },
        maxSizeMb: { type: Number, default: 100 },
        i18n: {
            type: Object,
            default: function () {
                return {
                    addVideoLabel: '',
                    retryLabel: '',
                    removeLabel: '',
                    statusQueued: '',
                    statusUploading: '',
                    statusProcessing: '',
                    statusReady: '',
                    statusFailed: '',
                    tooLargeTemplate: '',
                    processingTimeout: '',
                };
            }
        },
    },
    components: { draggable },
    data: function () {
        return {
            tiles: [],
            nextRecordId: 1,
            uploadInflight: false,
            pollIntervalMs: 2500,
            pollHandles: {},
            dragOpts: {
                delay: 400,
                delayOnTouchOnly: true,
                touchStartThreshold: 3,
                fallbackTolerance: 3,
            },
        };
    },
    mounted: function () {
        this.rehydrateFromDraftVideos();
    },
    watch: {
        draftPropertyId: function (newVal) {
            if (newVal) {
                this.pumpQueued();
            }
        },
    },
    beforeDestroy: function () {
        var keys = Object.keys(this.pollHandles);
        for (var i = 0; i < keys.length; i++) {
            clearInterval(this.pollHandles[keys[i]]);
        }
        this.pollHandles = {};
    },
    methods: {
        emitChanged: function () {
            var snapshot = this.tiles.map(function (t) {
                return {
                    recordId: t.recordId,
                    serverId: t.serverId,
                    status: t.status,
                    loadedBytes: t.loadedBytes || 0,
                    totalBytes: t.totalBytes || 0,
                };
            });
            this.$emit('videos-changed', snapshot);
        },
        onDragEnd: function () {
            // v-model has already mutated this.tiles to the new visual order; emit the snapshot.
            this.emitChanged();
        },
        statusCssState: function (status) {
            if (status === 'queued') return 'queued';
            if (status === 'uploading') return 'uploading';
            if (status === 'processing') return 'uploading';
            if (status === 'ready') return 'uploaded';
            if (status === 'failed') return 'failed';
            return 'queued';
        },
        statusLabel: function (tile) {
            if (tile.status === 'queued') return this.i18n.statusQueued || '';
            if (tile.status === 'uploading') {
                var base = this.i18n.statusUploading || '';
                return tile.progress > 0 ? base + ' ' + tile.progress + '%' : base;
            }
            if (tile.status === 'processing') return this.i18n.statusProcessing || '';
            if (tile.status === 'ready') return this.i18n.statusReady || '';
            if (tile.status === 'failed') return this.i18n.statusFailed || '';
            return '';
        },
        rehydrateFromDraftVideos: function () {
            var that = this;
            if (!Array.isArray(this.draftVideos) || this.draftVideos.length === 0) return;
            this.draftVideos.forEach(function (v) {
                var status = v.processing_status === 'ready' ? 'ready'
                    : (v.processing_status === 'failed' ? 'failed' : 'processing');
                var tile = {
                    recordId:         that.nextRecordId++,
                    serverId:         v.id,
                    originalFilename: v.original_filename || '',
                    status:           status,
                    progress:         0,
                    thumbnailUrl:     v.thumbnail_url || null,
                    url:              v.url || null,
                    errorMessage:     status === 'failed' ? that.i18n.statusFailed : null,
                    errorCode:        v.failure_reason || null,
                    pollErrors:       0,
                    file:             null,
                    xhr:              null,
                    removed:          false,
                    loadedBytes:      0,
                    totalBytes:       0,
                };
                that.tiles.push(tile);
                if (tile.status === 'processing') {
                    that.startPolling(tile);
                }
            });
            this.emitChanged();
        },
        openPicker: function () {
            this.$refs.picker.value = null;
            this.$refs.picker.click();
        },
        onFilePicked: function (event) {
            var file = event.target.files && event.target.files[0];
            event.target.value = null;
            if (!file) return;

            var sizeMb = Math.ceil(file.size / (1024 * 1024));
            if (file.size > this.maxSizeMb * 1024 * 1024) {
                var tmpl = this.i18n.tooLargeTemplate || '';
                var msg = tmpl
                    .replace(':size', String(sizeMb))
                    .replace(':limit', String(this.maxSizeMb));
                this.tiles.push({
                    recordId:         this.nextRecordId++,
                    serverId:         null,
                    originalFilename: file.name,
                    status:           'failed',
                    progress:         0,
                    thumbnailUrl:     null,
                    url:              null,
                    errorMessage:     msg || this.i18n.statusFailed,
                    errorCode:        'video_too_large',
                    pollErrors:       0,
                    file:             null,
                    xhr:              null,
                    removed:          false,
                    loadedBytes:      0,
                    totalBytes:       0,
                });
                this.emitChanged();
                return;
            }

            var tile = {
                recordId:         this.nextRecordId++,
                serverId:         null,
                originalFilename: file.name,
                status:           'queued',
                progress:         0,
                thumbnailUrl:     null,
                url:              null,
                errorMessage:     null,
                errorCode:        null,
                pollErrors:       0,
                file:             file,
                xhr:              null,
                removed:          false,
                loadedBytes:      0,
                totalBytes:       file.size || 0,
            };
            this.tiles.push(tile);
            this.emitChanged();

            if (!this.draftPropertyId) {
                this.$emit('need-draft');
                return;
            }
            this.pumpQueued();
        },
        pumpQueued: function () {
            if (this.uploadInflight) return;
            if (!this.draftPropertyId) return;
            for (var i = 0; i < this.tiles.length; i++) {
                if (this.tiles[i].status === 'queued' && this.tiles[i].file) {
                    this.startUpload(this.tiles[i]);
                    return;
                }
            }
        },
        startUpload: function (tile) {
            var that = this;
            tile.status = 'uploading';
            tile.progress = 0;
            this.uploadInflight = true;
            this.emitChanged();

            var url = this.uploadUrlBase.replace('__PROPERTY__', this.draftPropertyId);
            var formData = new FormData();
            formData.append('file', tile.file);

            tile.xhr = $.ajax({
                url: url,
                method: 'POST',
                data: formData,
                contentType: false,
                processData: false,
                dataType: 'json',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content },
                xhr: function () {
                    var x = $.ajaxSettings.xhr();
                    if (x && x.upload) {
                        x.upload.addEventListener('progress', function (e) {
                            if (e.lengthComputable) {
                                tile.progress = Math.min(99, Math.round((e.loaded / e.total) * 100));
                                tile.loadedBytes = e.loaded;
                                if (e.total) tile.totalBytes = e.total;
                                that.emitChanged();
                            }
                        });
                    }
                    return x;
                },
                success: function (resp) {
                    var newServerId = resp && resp.video ? resp.video.id : null;
                    if (tile.removed) {
                        // Tile gone before XHR returned. Clean the orphan row server-side.
                        if (newServerId) {
                            that.serverDeleteVideoId(newServerId);
                        }
                        return;
                    }
                    if (!resp || !resp.video) {
                        tile.status = 'failed';
                        tile.errorCode = (resp && resp.error) || 'upload_failed';
                        tile.errorMessage = that.i18n.statusFailed;
                        tile.file = null;
                        that.emitChanged();
                        return;
                    }
                    tile.serverId = newServerId;
                    tile.status = resp.video.processing_status === 'ready' ? 'ready'
                        : (resp.video.processing_status === 'failed' ? 'failed' : 'processing');
                    tile.thumbnailUrl = resp.video.thumbnail_url || null;
                    tile.url = resp.video.url || null;
                    tile.errorCode = resp.video.processing_status === 'failed'
                        ? (resp.video.failure_reason || 'processing_failed')
                        : null;
                    tile.errorMessage = tile.status === 'failed' ? that.i18n.statusFailed : null;
                    tile.file = null;
                    that.emitChanged();
                    if (tile.status === 'processing') {
                        that.startPolling(tile);
                    }
                },
                error: function (xhr) {
                    if (tile.removed) return;
                    tile.status = 'failed';
                    tile.file = null;
                    var body = xhr && xhr.responseJSON;
                    if (body && body.code === 'video_too_large') {
                        tile.errorCode = 'video_too_large';
                        var tmpl = that.i18n.tooLargeTemplate || '';
                        tile.errorMessage = tmpl
                            .replace(':size', String(body.size_mb || ''))
                            .replace(':limit', String(body.limit_mb || that.maxSizeMb));
                    } else {
                        tile.errorCode = (body && body.error) || 'upload_failed';
                        tile.errorMessage = that.i18n.statusFailed;
                    }
                    that.emitChanged();
                },
                complete: function () {
                    tile.xhr = null;
                    that.uploadInflight = false;
                    that.pumpQueued();
                },
            });
        },
        startPolling: function (tile) {
            var that = this;
            if (!tile.serverId || !this.draftPropertyId) return;
            this.stopPolling(tile.recordId);
            tile.pollErrors = 0;
            tile.pollStartedAt = Date.now();
            var url = this.statusUrlTemplate
                .replace('__PROPERTY__', this.draftPropertyId)
                .replace('__VIDEO__', tile.serverId);
            var handle = setInterval(function () {
                if (tile.removed) {
                    that.stopPolling(tile.recordId);
                    return;
                }
                // Wall-clock TTL: if a video stays in 'processing' for longer than
                // MAX_POLL_DURATION_MS, surface a clear failure with a retryable state
                // instead of polling indefinitely. Backend video is left alone.
                if (tile.pollStartedAt && (Date.now() - tile.pollStartedAt) > MAX_POLL_DURATION_MS) {
                    tile.status = 'failed';
                    tile.errorCode = 'processing_timeout';
                    tile.errorMessage = that.i18n.processingTimeout || that.i18n.statusFailed;
                    that.stopPolling(tile.recordId);
                    that.emitChanged();
                    return;
                }
                $.ajax({
                    url: url,
                    method: 'GET',
                    dataType: 'json',
                    success: function (resp) {
                        if (tile.removed) {
                            that.stopPolling(tile.recordId);
                            return;
                        }
                        if (!resp || !resp.video) {
                            tile.pollErrors++;
                            if (tile.pollErrors >= MAX_POLL_ERRORS) {
                                tile.status = 'failed';
                                tile.errorCode = 'status_unavailable';
                                tile.errorMessage = that.i18n.statusFailed;
                                that.stopPolling(tile.recordId);
                                that.emitChanged();
                            }
                            return;
                        }
                        tile.pollErrors = 0;
                        var s = resp.video.processing_status;
                        if (s === 'ready') {
                            tile.status = 'ready';
                            tile.thumbnailUrl = resp.video.thumbnail_url || null;
                            tile.url = resp.video.url || null;
                            tile.errorMessage = null;
                            tile.errorCode = null;
                            that.stopPolling(tile.recordId);
                            that.emitChanged();
                        } else if (s === 'failed') {
                            tile.status = 'failed';
                            tile.errorCode = resp.video.failure_reason || 'processing_failed';
                            tile.errorMessage = that.i18n.statusFailed;
                            that.stopPolling(tile.recordId);
                            that.emitChanged();
                        }
                    },
                    error: function () {
                        if (tile.removed) {
                            that.stopPolling(tile.recordId);
                            return;
                        }
                        tile.pollErrors++;
                        if (tile.pollErrors >= MAX_POLL_ERRORS) {
                            tile.status = 'failed';
                            tile.errorCode = 'status_unavailable';
                            tile.errorMessage = that.i18n.statusFailed;
                            that.stopPolling(tile.recordId);
                            that.emitChanged();
                        }
                    },
                });
            }, this.pollIntervalMs);
            this.pollHandles[tile.recordId] = handle;
        },
        stopPolling: function (recordId) {
            if (this.pollHandles[recordId]) {
                clearInterval(this.pollHandles[recordId]);
                delete this.pollHandles[recordId];
            }
        },
        serverDeleteVideoId: function (serverId) {
            if (!serverId || !this.draftPropertyId || !this.deleteUrlTemplate) return;
            var url = this.deleteUrlTemplate
                .replace('__PROPERTY__', this.draftPropertyId)
                .replace('__VIDEO__', serverId);
            $.ajax({
                url: url,
                method: 'DELETE',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content },
            });
        },
        retryTile: function (tile) {
            // Stop polling, server-delete if any, drop the failed tile, re-open picker.
            this.stopPolling(tile.recordId);
            if (tile.xhr && typeof tile.xhr.abort === 'function') {
                try { tile.xhr.abort(); } catch (e) {}
            }
            tile.removed = true;
            if (tile.serverId) {
                this.serverDeleteVideoId(tile.serverId);
            }
            var idx = this.tiles.indexOf(tile);
            if (idx >= 0) this.tiles.splice(idx, 1);
            this.emitChanged();
            this.openPicker();
        },
        removeTile: function (tile) {
            // Always mark removed first so a late XHR success cleans the server row.
            tile.removed = true;
            this.stopPolling(tile.recordId);
            if (tile.xhr && typeof tile.xhr.abort === 'function') {
                try { tile.xhr.abort(); } catch (e) {}
            }
            if (tile.serverId) {
                this.serverDeleteVideoId(tile.serverId);
            }
            var idx = this.tiles.indexOf(tile);
            if (idx >= 0) this.tiles.splice(idx, 1);
            this.emitChanged();
        },
        markQueuedAsFailed: function (reason) {
            var changed = false;
            for (var i = 0; i < this.tiles.length; i++) {
                var t = this.tiles[i];
                if (t.status === 'queued') {
                    t.status = 'failed';
                    t.errorCode = reason || 'draft_unavailable';
                    t.errorMessage = this.i18n.statusFailed;
                    t.file = null;
                    changed = true;
                }
            }
            if (changed) this.emitChanged();
        },
        getReadyVideoIds: function () {
            return this.tiles
                .filter(function (t) { return t.status === 'ready' && t.serverId; })
                .map(function (t) { return t.serverId; });
        },
        hasActivity: function () {
            return this.tiles.length > 0;
        },
        removeAllOnServer: function (onSuccess, onError) {
            var that = this;
            // Mark every tile removed + abort/stop activity so late callbacks no-op.
            this.tiles.forEach(function (t) {
                t.removed = true;
                that.stopPolling(t.recordId);
                if (t.xhr && typeof t.xhr.abort === 'function') {
                    try { t.xhr.abort(); } catch (e) {}
                }
            });
            var hasServerBacked = this.tiles.some(function (t) { return !!t.serverId; });
            if (!hasServerBacked || !this.draftPropertyId || !this.deleteAllUrlTemplate) {
                this.tiles = [];
                this.emitChanged();
                if (typeof onSuccess === 'function') onSuccess();
                return;
            }
            var url = this.deleteAllUrlTemplate.replace('__PROPERTY__', this.draftPropertyId);
            $.ajax({
                url: url,
                method: 'DELETE',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content },
                success: function () {
                    that.tiles = [];
                    that.emitChanged();
                    if (typeof onSuccess === 'function') onSuccess();
                },
                error: function () {
                    // Server still owns rows. Do NOT clear local tiles silently — let parent
                    // surface a failure so F5 can't rehydrate orphans.
                    if (typeof onError === 'function') onError();
                },
            });
        },
    },
};
</script>
